import { useState } from "react";

import ErrorIcon from "@uui/AlertCircle";
import CompleteIcon from "@uui/CheckCircle";
import DownArrow from "@uui/ChevronDown";
import UpArrow from "@uui/ChevronUp";
import MaximizeIcon from "@uui/Expand04";
import { filter } from "lodash/fp";

import {
  Box,
  CircularProgress,
  Collapse,
  Paper,
  Typography,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";

import useDailyReadPlayerDimensions from "@app/analytics/hooks/domHelpers/useDailyReadPlayerDimensions";
import { Upload } from "@app/analytics/upload/types";
import useUploadStore from "@app/analytics/upload/useUploadStore";

import { Column, Row } from "@rui/Stack";

const WidgetRow = ({
  status,
  name,
}: {
  status: Upload["status"];
  name: string;
}) => {
  return (
    <Row variant="left" spacing={1}>
      {status === "complete" && (
        <CompleteIcon
          sx={{ width: "16px", height: "16px", color: "shades.meadow.600" }}
        />
      )}
      {status === "error" && (
        <ErrorIcon sx={{ width: "16px", height: "16px", color: "#D32F2F" }} />
      )}
      {status === "loading" && (
        <CircularProgress
          variant="indeterminate"
          size={16}
          sx={{
            height: "16px",
            width: "16px",
            "& .MuiCircularProgress-circle": {
              strokeLinecap: "round",
              stroke: "#4C47CC",
            },
          }}
        />
      )}
      <Typography variant="bodySecondaryReg" color="shades.neutral.800">
        {name}
      </Typography>
    </Row>
  );
};

const MESSAGES = {
  loading: "Files uploading...",
  complete: "Uploads complete! Open to continue.",
  error: "Upload error. Open to continue",
};

const UploadWidget = () => {
  const { modal, openModal, uploads } = useUploadStore((state) => ({
    uploads: state.uploads,
    openModal: state.openModal,
    modal: state.modal,
  }));
  const [expanded, setExpanded] = useState(false);

  const handleToggleExpanded = () => setExpanded((expanded) => !expanded);

  const { width: rawDailyReadPlayerWidth } = useDailyReadPlayerDimensions();

  if (modal.open || uploads.length === 0 || !modal.minimized) {
    return null;
  }

  const errorUploads = filter({ status: "error" }, uploads);
  const loadingUploads = filter({ status: "loading" }, uploads);

  let title = MESSAGES.loading;
  if (errorUploads.length) {
    title = MESSAGES.error;
  } else if (errorUploads.length === 0 && loadingUploads.length === 0) {
    title = MESSAGES.complete;
  }

  // extra 12px of padding between the elements when the player is showing
  const dailyReadPlayerWidth =
    rawDailyReadPlayerWidth > 0 ? rawDailyReadPlayerWidth + 12 : 0;

  return (
    <Box
      position="fixed"
      bottom={0}
      right={20 + dailyReadPlayerWidth}
      zIndex={10}
      width={420}
    >
      <Paper
        sx={{
          padding: "8px 16px",
          borderRadius: "8px 8px 0 0",
          border: "1px solid rgba(0,0,0,0.08)",
          borderBottom: "none",
        }}
        elevation={10}
      >
        <Row variant="apart" spacing={1}>
          <Typography variant="bodySecondaryMed">{title}</Typography>
          <Row variant="right" spacing={1}>
            <IconButton onClick={handleToggleExpanded}>
              {expanded ? <DownArrow /> : <UpArrow />}
            </IconButton>
            <IconButton onClick={openModal}>
              <MaximizeIcon />
            </IconButton>
          </Row>
        </Row>
        <Collapse in={expanded}>
          <Column
            spacing={1}
            sx={{
              borderTop: expanded
                ? (theme) => `1px solid ${theme.palette.shades.neutral[100]}`
                : "none",
              padding: "12px 0",
            }}
          >
            {uploads.map((upload) => (
              <WidgetRow
                key={upload.id}
                status={upload.status}
                name={upload.name}
              />
            ))}
          </Column>
        </Collapse>
      </Paper>
    </Box>
  );
};

export default UploadWidget;
